/**
 * Client for the SQLite worker.
 *
 * The connection itself lives in `worker.ts` — see that file for why it has
 * to. This side is only a request/response channel, and it deliberately keeps
 * `run` and `all` returning promises with the same signatures they had when
 * sqlite3 ran inline, so the twelve modules that query the database did not
 * have to change when it moved threads.
 */
import type { DbCommand, DbInfo, DbRequest, DbResponse, Statement } from './worker';

export type { DbInfo, Statement };

interface Pending {
  resolve: (value: unknown) => void;
  reject: (reason: Error) => void;
}

const pending = new Map<number, Pending>();
let nextId = 1;
let worker: Worker | null = null;

function channel(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('./worker.ts', import.meta.url), {
    type: 'module',
    name: 'kapture-sqlite',
  });
  worker.onmessage = (event: MessageEvent<DbResponse>) => {
    const message = event.data;
    const waiting = pending.get(message.id);
    if (!waiting) return;
    pending.delete(message.id);
    if (message.ok) waiting.resolve(message.result);
    else waiting.reject(new Error(message.error));
  };
  worker.onerror = (event) => {
    // The worker died. Every caller waiting on it must fail rather than hang,
    // and the next call gets a fresh worker.
    const error = new Error(`sqlite worker failed: ${event.message}`);
    for (const waiting of pending.values()) waiting.reject(error);
    pending.clear();
    worker?.terminate();
    worker = null;
  };
  return worker;
}

function send<T>(command: DbCommand): Promise<T> {
  const id = nextId++;
  const target = channel();
  return new Promise<T>((resolve, reject) => {
    pending.set(id, { resolve: resolve as (value: unknown) => void, reject });
    target.postMessage({ ...command, id } as DbRequest);
  });
}

/**
 * Open the database and report what is backing it.
 *
 * Returns the VFS in use so a caller can tell durable storage from the
 * in-memory fallback — the difference between an offline-first app and one
 * that loses the day's work on reload.
 */
export function openDatabase(): Promise<DbInfo> {
  return send<DbInfo>({ kind: 'open' });
}

/** Run a one-shot statement. */
export function run(sql: string, bind?: unknown[]): Promise<void> {
  return send<void>({ kind: 'exec', sql, bind });
}

/** Run a query and return rows as plain objects. */
export function all<T = Record<string, unknown>>(
  sql: string,
  bind?: unknown[],
): Promise<T[]> {
  return send<T[]>({ kind: 'exec', sql, bind, rows: true });
}

/**
 * Run statements in one transaction.
 *
 * Every statement travels in a single message, so BEGIN and COMMIT bracket
 * only these statements. Issuing them as separate `run` calls does not: the
 * worker interleaves requests from other callers between them, which would put
 * unrelated writes inside the transaction and roll them back on failure.
 */
export function tx(statements: Statement[]): Promise<void> {
  return send<void>({ kind: 'tx', statements });
}
